import { useEffect, useMemo, useState } from 'react';
import SearchableSelect from '@/components/base/SearchableSelect';
import { getAllWarehouses } from '@/api/warehouse.api';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import {
  scrapApi, SCRAP_REASON_LABEL, num,
  type ScrapEntry, type ScrapReason, type ProductionScrapOption, type GrnScrapOption, type WriteOffOption,
} from '@/api/scrap.api';
import { Modal, Field, inputCls, inr, qtyText, noNegativeKeys, dateText } from './scrapUi';

// Raise a scrap entry by hand: production rejections (QC disposition from the order), incoming material
// found defective, or stock written off. Process scrap is recorded by itself when an order completes.

type ManualSource = 'PRODUCTION' | 'GRN' | 'WRITE_OFF';

const SOURCES: { value: ManualSource; label: string; hint: string; icon: string }[] = [
  { value: 'PRODUCTION', label: 'Production rejection', hint: 'Rejected units of an order', icon: 'ri-settings-3-line' },
  { value: 'GRN', label: 'Incoming rejection', hint: 'Defective material of a GRN', icon: 'ri-truck-line' },
  { value: 'WRITE_OFF', label: 'Write-off', hint: 'Expired / obsolete stock', icon: 'ri-archive-line' },
];

const DEFAULT_REASON: Record<ManualSource, ScrapReason> = {
  PRODUCTION: 'DIMENSIONAL_ERROR',
  GRN: 'MATERIAL_DEFECT',
  WRITE_OFF: 'EXPIRED',
};

const today = () => new Date().toISOString().slice(0, 10);

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (entry: ScrapEntry) => void;
  /** Opened from a production order: source, order and stage are set */
  preset?: { productionOrderId: string; stageNumber?: number };
}

export default function NewScrapModal({ open, onClose, onCreated, preset }: Props) {
  const toast = useToast();
  const { selectedWarehouseId } = useWarehouseStore();
  const [source, setSource] = useState<ManualSource>('PRODUCTION');
  const [orders, setOrders] = useState<ProductionScrapOption[]>([]);
  const [grns, setGrns] = useState<GrnScrapOption[]>([]);
  const [stockItems, setStockItems] = useState<WriteOffOption[]>([]);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [poId, setPoId] = useState('');
  const [stageNumber, setStageNumber] = useState('');
  const [grnId, setGrnId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState<ScrapReason>('DIMENSIONAL_ERROR');
  const [scrapRate, setScrapRate] = useState('');
  const [location, setLocation] = useState('Scrap Yard');
  const [entryDate, setEntryDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [deductStock, setDeductStock] = useState(true);

  // Fresh form on every opening
  useEffect(() => {
    if (!open) return;
    setSource('PRODUCTION');
    setPoId(preset?.productionOrderId || '');
    setStageNumber(preset?.stageNumber != null ? String(preset.stageNumber) : '');
    setGrnId('');
    setWarehouseId(selectedWarehouseId || '');
    setItemId('');
    setQty('');
    setReason('DIMENSIONAL_ERROR');
    setScrapRate('');
    setLocation('Scrap Yard');
    setEntryDate(today());
    setNotes('');
    setDeductStock(true);
    setErrors({});
    getAllWarehouses()
      .then((r) => setWarehouses((r.data || []).filter((w: { is_active?: boolean }) => w.is_active !== false)))
      .catch(() => setWarehouses([]));
  }, [open, preset?.productionOrderId, preset?.stageNumber, selectedWarehouseId]);

  // What the chosen source can pick from
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const done = () => !cancelled && setLoading(false);
    if (source === 'PRODUCTION') {
      scrapApi.productionOptions(preset ? undefined : selectedWarehouseId || undefined)
        .then((r) => !cancelled && setOrders(r.data || []))
        .catch(() => !cancelled && setOrders([]))
        .finally(done);
    } else if (source === 'GRN') {
      scrapApi.grnOptions(selectedWarehouseId || undefined)
        .then((r) => !cancelled && setGrns(r.data || []))
        .catch(() => !cancelled && setGrns([]))
        .finally(done);
    } else if (warehouseId) {
      scrapApi.writeOffOptions(warehouseId)
        .then((r) => !cancelled && setStockItems(r.data || []))
        .catch(() => !cancelled && setStockItems([]))
        .finally(done);
    } else {
      setStockItems([]);
      done();
    }
    return () => { cancelled = true; };
  }, [open, source, warehouseId, selectedWarehouseId, preset]);

  const chooseSource = (s: ManualSource) => {
    setSource(s);
    setItemId('');
    setQty('');
    setScrapRate('');
    setReason(DEFAULT_REASON[s]);
    setErrors({});
  };

  const order = orders.find((o) => o.id === poId);
  const stage = order?.stages.find((s) => String(s.stageNumber) === stageNumber);
  const grn = grns.find((g) => g.id === grnId);
  const grnLine = grn?.items.find((i) => i.itemId === itemId);
  const stockItem = stockItems.find((i) => i.id === itemId);

  // Stage with rejections left, picked by itself when there is just one
  useEffect(() => {
    if (source !== 'PRODUCTION' || !order || stageNumber) return;
    const open = order.stages.filter((s) => s.pendingQty > 0);
    if (open.length === 1) setStageNumber(String(open[0].stageNumber));
  }, [source, order, stageNumber]);

  // The item's own scrap value is the default rate
  const itemScrapRate = source === 'PRODUCTION' ? num(order?.scrap_rate) : source === 'GRN' ? num(grnLine?.scrapRate) : num(stockItem?.scrap_rate);
  const unit = source === 'PRODUCTION' ? order?.unit : source === 'GRN' ? grnLine?.unit : stockItem?.unit;
  const maxQty = source === 'PRODUCTION'
    ? stage?.pendingQty ?? null
    : source === 'GRN'
      ? grnLine ? Math.max(0, num(grnLine.qty) - num(grnLine.scrappedQty)) : null
      : stockItem ? Math.max(0, num(stockItem.stock) - num(stockItem.pending_write_off)) : null;
  const rate = scrapRate === '' ? itemScrapRate : Number(scrapRate) || 0;
  const estValue = (Number(qty) || 0) * rate;

  const orderOptions = useMemo(() => orders.map((o) => {
    const pending = o.stages.reduce((a, s) => a + s.pendingQty, 0);
    return { value: o.id, label: `${o.po_number} · ${o.product_name}`, sublabel: `${qtyText(pending)} ${o.unit} rejected, not yet scrapped`, badge: o.status };
  }), [orders]);
  const grnOptions = useMemo(() => grns.map((g) => ({
    value: g.id,
    label: `${g.grn_number}${g.supplier_name ? ` · ${g.supplier_name}` : ''}`,
    sublabel: `${dateText(g.date)} · ${g.items.length} item${g.items.length === 1 ? '' : 's'}`,
  })), [grns]);
  const stockOptions = useMemo(() => stockItems.map((i) => ({
    value: i.id,
    label: `${i.code ? `${i.code} · ` : ''}${i.name}`,
    sublabel: `In stock ${qtyText(i.stock)} ${i.unit}${num(i.pending_write_off) > 0 ? ` · ${qtyText(i.pending_write_off)} already waiting to be written off` : ''}`,
  })), [stockItems]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (source === 'PRODUCTION') {
      if (!poId) e.poId = 'Choose the production order';
      if (!stageNumber) e.stageNumber = 'Choose the stage the units were rejected at';
    }
    if (source === 'GRN') {
      if (!grnId) e.grnId = 'Choose the GRN';
      if (!itemId) e.itemId = 'Choose the rejected item';
    }
    if (source === 'WRITE_OFF') {
      if (!warehouseId) e.warehouseId = 'Choose the warehouse';
      if (!itemId) e.itemId = 'Choose the item to write off';
    }
    const q = Number(qty);
    if (!(q > 0)) e.qty = 'Enter the scrap quantity';
    else if (maxQty != null && q > maxQty + 1e-6) e.qty = `Only ${qtyText(maxQty)} ${unit || ''} available`;
    if (scrapRate !== '' && !(Number(scrapRate) >= 0)) e.scrapRate = 'Enter 0 or more';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await scrapApi.create({
        sourceType: source,
        qty: Number(qty),
        reason,
        notes: notes.trim() || undefined,
        scrapRate: scrapRate === '' ? null : Number(scrapRate),
        scrapLocation: location.trim() || undefined,
        entryDate,
        ...(source === 'PRODUCTION' ? { productionOrderId: poId, stageNumber: Number(stageNumber) } : {}),
        ...(source === 'GRN' ? { grnId, itemId, deductStock } : {}),
        ...(source === 'WRITE_OFF' ? { warehouseId, itemId } : {}),
      });
      toast.success(res.message || 'Scrap entry raised');
      onCreated(res.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not raise the scrap entry');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const allowedSources = preset ? SOURCES.filter((s) => s.value === 'PRODUCTION') : SOURCES;

  return (
    <Modal
      title="New Scrap Entry"
      subtitle="Waits for a production manager / admin to value and approve it"
      onClose={onClose}
      width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-white cursor-pointer">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={saving}
            className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer flex items-center gap-2">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving…</> : <><i className="ri-delete-bin-6-line" /> Raise Scrap Entry</>}
          </button>
        </>
      )}
    >
      {/* Source */}
      <div className={`grid gap-2 ${allowedSources.length === 3 ? 'grid-cols-3' : 'grid-cols-1'}`}>
        {allowedSources.map((s) => (
          <button key={s.value} type="button" onClick={() => chooseSource(s.value)}
            className={`text-left p-3 rounded-xl border transition-colors cursor-pointer ${source === s.value ? 'border-[#4f46e5] bg-indigo-50/60' : 'border-[#e2e8f0] hover:border-[#4f46e5]/40'}`}>
            <p className={`text-sm font-semibold flex items-center gap-1.5 ${source === s.value ? 'text-[#4f46e5]' : 'text-[#1e293b]'}`}>
              <i className={s.icon} /> {s.label}
            </p>
            <p className="text-[11px] text-[#94a3b8] mt-0.5">{s.hint}</p>
          </button>
        ))}
      </div>
      {!preset && (
        <p className="text-[11px] text-[#94a3b8] -mt-2">
          Process scrap (cutting / machining waste) is recorded by itself when a production order completes, from the BOM scrap %.
        </p>
      )}

      {/* Source document */}
      {source === 'PRODUCTION' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Production Order" required error={errors.poId}>
            <SearchableSelect
              value={poId}
              onChange={(v) => { setPoId(v); setStageNumber(''); }}
              options={orderOptions}
              placeholder={loading ? 'Loading…' : orders.length ? '— Select order —' : 'No order with unscrapped rejections'}
              searchPlaceholder="Search order or product…"
              disabled={!!preset}
            />
          </Field>
          <Field label="Stage" required error={errors.stageNumber}>
            <select value={stageNumber} onChange={(e) => setStageNumber(e.target.value)} disabled={!order} className={inputCls}>
              <option value="">{order ? '— Select stage —' : 'Choose the order first'}</option>
              {order?.stages.map((s) => (
                <option key={s.stageNumber} value={s.stageNumber} disabled={s.pendingQty <= 0}>
                  Stage {s.stageNumber} · {s.stageName} — {qtyText(s.pendingQty)} of {qtyText(s.rejectedQty)} rejected left
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {source === 'GRN' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="GRN" required error={errors.grnId}>
              <SearchableSelect
                value={grnId}
                onChange={(v) => { setGrnId(v); setItemId(''); }}
                options={grnOptions}
                placeholder={loading ? 'Loading…' : '— Select GRN —'}
                searchPlaceholder="Search GRN or supplier…"
              />
            </Field>
            <Field label="Rejected Item" required error={errors.itemId}>
              <select value={itemId} onChange={(e) => setItemId(e.target.value)} disabled={!grn} className={inputCls}>
                <option value="">{grn ? '— Select item —' : 'Choose the GRN first'}</option>
                {grn?.items.map((i) => (
                  <option key={i.itemId} value={i.itemId}>
                    {i.itemName} — received {qtyText(i.qty)} {i.unit}{num(i.scrappedQty) > 0 ? `, ${qtyText(i.scrappedQty)} already scrapped` : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <label className="flex items-start gap-2 text-xs text-[#475569] cursor-pointer">
            <input type="checkbox" checked={deductStock} onChange={(e) => setDeductStock(e.target.checked)} className="mt-0.5 w-3.5 h-3.5 accent-[#4f46e5]" />
            <span>
              The material was taken into stock — take it out of the warehouse when approved.
              <span className="block text-[#94a3b8]">Untick when it was held at inspection and never entered stock. To send it back to the supplier, use a Purchase Return instead.</span>
            </span>
          </label>
        </>
      )}

      {source === 'WRITE_OFF' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Warehouse" required error={errors.warehouseId}>
            <select value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); setItemId(''); }} className={inputCls}>
              <option value="">— Select warehouse —</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
          <Field label="Item" required error={errors.itemId}>
            <SearchableSelect
              value={itemId}
              onChange={setItemId}
              options={stockOptions}
              placeholder={!warehouseId ? 'Choose the warehouse first' : loading ? 'Loading…' : stockItems.length ? '— Select item —' : 'No stock in this warehouse'}
              searchPlaceholder="Search item…"
              disabled={!warehouseId}
            />
          </Field>
        </div>
      )}

      {/* Quantity, reason, value */}
      <div className="grid grid-cols-3 gap-3">
        <Field label={`Quantity${unit ? ` (${unit})` : ''}`} required error={errors.qty} hint={maxQty != null ? `Up to ${qtyText(maxQty)}` : undefined}>
          <input type="number" min="0" step="any" value={qty} onKeyDown={noNegativeKeys}
            onChange={(e) => setQty(e.target.value.replace(/-/g, ''))} className={inputCls} placeholder="0" />
        </Field>
        <Field label="Reason" required>
          <select value={reason} onChange={(e) => setReason(e.target.value as ScrapReason)} className={inputCls}>
            {(Object.keys(SCRAP_REASON_LABEL) as ScrapReason[])
              .filter((r) => r !== 'PROCESS_WASTE')
              .map((r) => <option key={r} value={r}>{SCRAP_REASON_LABEL[r]}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input type="date" value={entryDate} max={today()} onChange={(e) => setEntryDate(e.target.value)} className={inputCls} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Scrap Value / Unit (₹)"
          error={errors.scrapRate}
          hint={`What one unit fetches as scrap. Blank = the item's value (${inr(itemScrapRate)}); 0 = zero-value, destroyed on approval.`}
        >
          <input type="number" min="0" step="any" value={scrapRate} onKeyDown={noNegativeKeys}
            onChange={(e) => setScrapRate(e.target.value.replace(/-/g, ''))} className={inputCls} placeholder={String(itemScrapRate)} />
        </Field>
        <Field label="Scrap Location" hint="Where it is kept, e.g. Scrap Yard A">
          <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} maxLength={80} />
        </Field>
      </div>

      <Field label="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500}
          placeholder="What happened, who found it…" className={`${inputCls} h-auto py-2 resize-none`} />
      </Field>

      {Number(qty) > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs">
          <span className="text-[#64748b]">Estimated scrap value</span>
          <span className="font-semibold text-[#1e293b]">
            {qtyText(qty)} {unit || ''} × {inr(rate)} = <span className="text-[#4f46e5]">{inr(estValue)}</span>
            {estValue === 0 && <span className="ml-2 font-normal text-amber-600">zero value — destroyed on approval</span>}
          </span>
        </div>
      )}
    </Modal>
  );
}
